import type {
  ActionResult,
  BroadcastResponse,
  BroadcastTarget,
  ChatUserRef,
  Platform,
} from '@multimod/shared';

export interface BanInput {
  platform: Platform;
  liveChatId: string;
  target: ChatUserRef;
  reason?: string;
}

export interface TimeoutInput extends BanInput {
  durationSeconds: number;
}

export interface DeleteInput {
  platform: Platform;
  liveChatId: string;
  messageExternalId: string;
}

export interface BroadcastInput {
  text: string;
  targets: BroadcastTarget[];
}

async function postJson(path: string, body: unknown): Promise<ActionResult> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 401) return { ok: false, errorCode: 'unauthenticated' };
  if (res.status === 429) return { ok: false, errorCode: 'rate_limited' };
  try {
    return (await res.json()) as ActionResult;
  } catch {
    return { ok: false, errorCode: 'network_error' };
  }
}

export function banApi(input: BanInput): Promise<ActionResult> {
  return postJson('/moderation/ban', input);
}

export function timeoutApi(input: TimeoutInput): Promise<ActionResult> {
  return postJson('/moderation/timeout', input);
}

export function deleteApi(input: DeleteInput): Promise<ActionResult> {
  return postJson('/moderation/delete', input);
}

export async function broadcastApi(input: BroadcastInput): Promise<BroadcastResponse> {
  const res = await fetch('/moderation/broadcast', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    // #74: não fabricar um BroadcastResponse "fingido" — broadcastId vazio viola
    // BroadcastResponseSchema, e mapear o 429 local (nosso bucket) pra
    // 'rate_limited_upstream' dá diagnóstico errado (≠ Twitch/YouTube me derrubou).
    // Lançar deixa o onError do useBroadcast marcar todos os targets como failed
    // e o erro de transporte (5xx/network) aparecer no Sentry (P8).
    throw new Error(`broadcast_http_${res.status}`);
  }
  return (await res.json()) as BroadcastResponse;
}
